import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalContent } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Camera,
  Check,
  CircleAlert,
  ClipboardPaste,
  Crown,
  FileText,
  ListChecks,
  Plus,
  RefreshCw,
  Repeat,
  ScanText,
  Upload,
  X,
  ZoomIn,
} from 'lucide-react';

import { trackEvent } from '../../utils/gt';
import { numberFormatter } from '../../utils/calorieLog';
import { useWakeLock } from '../../utils/useWakeLock';
import {
  IMPORT_LIMITS,
  MEAL_PRESETS,
  activeFoods,
  allMeals,
  countPdfPages,
  downscaleImage,
  draftFromImport,
  draftProblem,
  emptyMeal,
  fileProblem,
  hasOptions,
  isUnread,
  manualDraft,
  menuTotals,
  planPayload,
  readPlan,
  substitutesFor,
  withActiveFoods,
} from '../../utils/dietImport';
import { removeFoodAt, replaceFoodAt } from '../../utils/foodEdit';
import { FoodEditSheet } from './FoodEditSheet';
import { aiLimitText } from '../../utils/aiLimits';
import { usePageTopOn } from '../../utils/pageTop';
import { MealDraftCard } from './MealDraftCard';
import '../../styles/pages/diet-import.css';
import { PreviewArt } from '../app/PreviewArt';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT_KEYS = ['monShort', 'tueShort', 'wedShort', 'thuShort', 'friShort', 'satShort', 'sunShort'];
const READ_STEPS = ['readStepSend', 'readStepMeals', 'readStepFoods', 'readStepNumbers'];
// When each reading step lights up, in seconds: the API answers in one go (10 to 20 s for a photo)
const READ_STEP_AT = [0, 3, 8, 14];

const formatSize = (bytes, locale) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toLocaleString(locale, { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024)).toLocaleString(locale)} KB`;

/** The plan as it was sent, beside what was read: the photo (a meal's part lit while it's pointed at), the PDF, or the text. */
const OriginalPanel = ({ source, highlight, ix }) => {
  if (!source) return null;
  if (source.kind === 'image') {
    return (
      <section className="mt-di-original" aria-label={ix('originalTitle')}>
        <div className="mt-di-original__head">
          <h2 className="mt-di-card-title">{ix('originalTitle')}</h2>
          <a className="mt-icon-btn" href={source.url} target="_blank" rel="noopener noreferrer" aria-label={ix('originalZoom')}>
            <ZoomIn aria-hidden="true" />
          </a>
        </div>
        <figure className="mt-di-original__frame">
          <img src={source.url} alt={ix('originalAlt')} />
          {highlight && (
            <span
              className="mt-di-original__band"
              style={{ top: `${highlight.top * 100}%`, height: `${Math.max(highlight.bottom - highlight.top, 0.03) * 100}%` }}
              aria-hidden="true"
            />
          )}
        </figure>
        <p className="mt-di-original__note">{ix('originalNote', { name: source.name })}</p>
      </section>
    );
  }
  if (source.kind === 'pdf') {
    return (
      <section className="mt-di-original" aria-label={ix('originalTitle')}>
        <h2 className="mt-di-card-title">{ix('originalTitle')}</h2>
        <div className="mt-di-file mt-di-file--flat">
          <span className="mt-di-file__icon" aria-hidden="true">
            <FileText />
          </span>
          <span className="mt-di-file__text">
            <span className="mt-di-file__name">{source.name}</span>
            <span className="mt-di-file__meta">{source.pages ? ix('pdfPages', { count: source.pages }) : 'PDF'}</span>
          </span>
        </div>
        <a className="mt-btn mt-btn--quiet mt-di-original__open" href={source.url} target="_blank" rel="noopener noreferrer">
          <FileText aria-hidden="true" />
          {ix('originalOpenPdf')}
        </a>
        <p className="mt-di-original__note">{ix('originalPdfNote')}</p>
      </section>
    );
  }
  return (
    <section className="mt-di-original mt-di-original--text" aria-label={ix('originalTitle')}>
      <h2 className="mt-di-card-title">{ix('originalTitle')}</h2>
      <pre className="mt-di-original__text">{source.text}</pre>
    </section>
  );
};

/**
 * A diet plan from outside the AI. "Importar meu plano": the photo, PDF or text is read by the API (nothing saved),
 * then checked beside the original and confirmed meal by meal; a meal the photo missed is built by hand in the same
 * place. "Montar à mão": the same builder, starting from the meals of the day. Saving replaces the plan there is,
 * after asking. Free accounts see the screen behind the Pro card.
 */
export const DietCreate = ({ mode, isPro, isProKnown, hasPlan, onBack, onSave, onUpgrade, onDirtyChange }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ix = (key, options) => t(`pages.DietImport.${key}`, options);
  const diet = (key, options) => t(`pages.DietPlan.${key}`, options);
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const locale = language === 'pt' ? 'pt-BR' : 'en-US';
  const formatNumber = numberFormatter(language);
  const isImport = mode === 'import';

  // upload → reading → review (or unreadable); a hand-built plan: pick → review
  const [stage, setStage] = useState(isImport ? 'upload' : 'pick');
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [pdfPages, setPdfPages] = useState(null);
  const [originalSize, setOriginalSize] = useState(0);
  const [isTextMode, setIsTextMode] = useState(false);
  const [text, setText] = useState('');
  const [consent, setConsent] = useState(false);
  const [isConsentMissing, setIsConsentMissing] = useState(false);
  const [problem, setProblem] = useState(null);
  const [unreadReason, setUnreadReason] = useState(null);
  const [readSeconds, setReadSeconds] = useState(0);
  const [source, setSource] = useState(null);
  const abortRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [draft, setDraft] = useState(null);
  const [groupIndex, setGroupIndex] = useState(0);
  const [openMealId, setOpenMealId] = useState(null);
  const [hoveredMealId, setHoveredMealId] = useState(null);
  const [alternate, setAlternate] = useState(false);
  const [phoneTab, setPhoneTab] = useState('read');
  const [picked, setPicked] = useState(() => new Set(['breakfast', 'lunch', 'afternoonSnack', 'dinner']));
  const [customMeal, setCustomMeal] = useState('');
  const [foodSheet, setFoodSheet] = useState(null);
  const [isFoodSheetOpen, setIsFoodSheetOpen] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useWakeLock(stage === 'reading');

  // The photo lives only in this browser while the plan is checked (never kept by the API): its object URL is revoked
  // when it's replaced and when the flow ends (saved, left, unmounted)
  useEffect(() => () => fileUrl && URL.revokeObjectURL(fileUrl), [fileUrl]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const isDirty = !!draft && allMeals(draft).some((meal) => activeFoods(meal).length > 0 || meal.confirmed);
  useEffect(() => {
    onDirtyChange?.(isDirty && !isSaving);
  }, [isDirty, isSaving, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  useEffect(() => {
    if (stage !== 'reading') return undefined;
    setReadSeconds(0);
    const started = Date.now();
    const timer = setInterval(() => setReadSeconds((Date.now() - started) / 1000), 500);
    return () => clearInterval(timer);
  }, [stage]);

  // Each stage is a screen of its own and opens at the top, after it's rendered
  usePageTopOn(stage);

  // ── Upload ──────────────────────────────────────────────────────────

  const chooseFile = async (chosen) => {
    if (!chosen) return;
    setProblem(null);
    setIsTextMode(false);
    setOriginalSize(chosen.size);
    const isPdf = chosen.type === 'application/pdf';
    const pages = isPdf ? await countPdfPages(chosen) : null;
    const ready = !isPdf && chosen.type.startsWith('image/') ? await downscaleImage(chosen) : chosen;
    const found = fileProblem(ready, pages);
    setPdfPages(pages);
    setFile(ready);
    setFileUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return URL.createObjectURL(ready);
    });
    if (found) setProblem(found);
  };

  const clearFile = () => {
    setFile(null);
    setFileUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return null;
    });
    setPdfPages(null);
    setProblem(null);
  };

  const onDrop = (event) => {
    event.preventDefault();
    chooseFile(event.dataTransfer?.files?.[0]);
  };

  const canRead = isTextMode ? text.trim().length > 0 && text.length <= IMPORT_LIMITS.textMaxChars : !!file && !problem;

  const startReading = async () => {
    if (!isPro) {
      onUpgrade();
      return;
    }
    if (!consent) {
      setIsConsentMissing(true);
      toast.error(ix('consentRequired'), { toastId: 'diet-import-consent' });
      return;
    }
    if (!canRead) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setProblem(null);
    setStage('reading');
    const sent = isTextMode ? { kind: 'text', text } : { kind: file.type === 'application/pdf' ? 'pdf' : 'image', url: fileUrl, name: file.name, pages: pdfPages };
    try {
      const data = await readPlan({ file: isTextMode ? null : file, text: isTextMode ? text : null, lang: language, signal: controller.signal });
      // Sent and read: the downscaled file isn't needed any more (the review shows the object URL, until it's revoked)
      setFile(null);
      const read = draftFromImport(data);
      const readMeals = allMeals(read).filter((meal) => !isUnread(meal));
      setSource(sent);
      if (data.unreadable || readMeals.length === 0) {
        setUnreadReason(data.unreadableReason || null);
        setStage('unreadable');
        trackEvent('diet_plan_import_unreadable', 'Diet Plan - Import Unreadable', 'diet_plan', true);
        return;
      }
      trackEvent('diet_plan_import_read', 'Diet Plan - Import Read', 'diet_plan', true);
      setDraft(read);
      setGroupIndex(0);
      setAlternate(false);
      setPhoneTab('read');
      setOpenMealId(read.groups[0].meals.find((meal) => !meal.confirmed)?.id || null);
      setStage('review');
    } catch (error) {
      if (error?.name === 'AbortError') {
        setStage('upload');
        return;
      }
      const code = error?.code || 'READ_FAILED';
      trackEvent('diet_plan_import_error', `Diet Plan - Import Error: ${code}`, 'diet_plan', true);
      if (code === 'PRO_REQUIRED') onUpgrade();
      if (code === 'CONSENT_REQUIRED') setIsConsentMissing(true);
      setProblem({ code, ...(error?.details || {}) });
      setStage('upload');
    } finally {
      abortRef.current = null;
    }
  };

  const cancelReading = () => abortRef.current?.abort();

  const tryAgainWith = (next) => {
    setStage('upload');
    setUnreadReason(null);
    if (next === 'text') {
      clearFile();
      setIsTextMode(true);
    } else if (next === 'photo') {
      clearFile();
      // The camera on phones, the file dialog on desktop
      setTimeout(() => (isDesktop ? fileInputRef : cameraInputRef).current?.click(), 0);
    } else if (next === 'pdf') {
      clearFile();
      setTimeout(() => fileInputRef.current?.click(), 0);
    }
  };

  // ── The draft ───────────────────────────────────────────────────────

  const meals = useMemo(() => draft?.groups[groupIndex]?.meals || [], [draft, groupIndex]);
  const everyMeal = draft ? allMeals(draft) : [];
  const confirmedCount = everyMeal.filter((meal) => meal.confirmed).length;
  const allConfirmed = everyMeal.length > 0 && confirmedCount === everyMeal.length;
  const totals = menuTotals(meals);
  const flags = meals.flatMap(activeFoods).filter((food) => food.assumed || food.source === 'estimate');
  const assumedCount = flags.filter((food) => food.assumed).length;
  const estimateCount = flags.filter((food) => food.source === 'estimate' && !food.assumed).length;
  const unreadNames = everyMeal.filter(isUnread).map((meal) => meal.name);

  const updateMeals = (change) =>
    setDraft((current) => ({
      ...current,
      groups: current.groups.map((candidate, index) => (index === groupIndex ? { ...candidate, meals: change(candidate.meals) } : candidate)),
    }));
  const updateMeal = (id, change) => updateMeals((list) => list.map((meal) => (meal.id === id ? change(meal) : meal)));

  const confirmMeal = (id) => {
    trackEvent('diet_plan_import_meal_confirm', 'Diet Plan - Confirm Meal', 'diet_plan', true);
    const updated = meals.map((meal) => (meal.id === id ? { ...meal, confirmed: true } : meal));
    updateMeals(() => updated);
    const at = updated.findIndex((meal) => meal.id === id);
    const next = [...updated.slice(at + 1), ...updated.slice(0, at)].find((meal) => !meal.confirmed);
    setOpenMealId(next?.id || null);
    // A weekly plan moves on to its next menu once this one is done
    if (!next && draft.groups.length > 1) {
      const nextGroup = draft.groups.findIndex((candidate, index) => index !== groupIndex && candidate.meals.some((meal) => !meal.confirmed));
      if (nextGroup !== -1) {
        setGroupIndex(nextGroup);
        setOpenMealId(draft.groups[nextGroup].meals.find((meal) => !meal.confirmed)?.id || null);
      }
    }
    if (next) {
      setTimeout(() => document.getElementById(`mt-di-meal-${next.id}`)?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }), 50);
    }
  };

  const removeMeal = (id) => {
    updateMeals((list) => list.filter((meal) => meal.id !== id));
    if (openMealId === id) setOpenMealId(null);
  };

  const addMeal = (name, time = null) => {
    const meal = emptyMeal(name, time);
    updateMeals((list) => [...list, meal]);
    setOpenMealId(meal.id);
  };

  const startManual = () => {
    const names = MEAL_PRESETS.filter((preset) => picked.has(preset.key)).map((preset) => emptyMeal(ix(`meal_${preset.key}`), preset.time));
    if (customMeal.trim()) names.push(emptyMeal(customMeal.trim(), null));
    if (!names.length) {
      toast.error(ix('pickMealsRequired'), { toastId: 'diet-manual-pick' });
      return;
    }
    const fresh = manualDraft(names);
    setDraft(fresh);
    setGroupIndex(0);
    setOpenMealId(names[0].id);
    setStage('review');
  };

  // ── Foods ───────────────────────────────────────────────────────────

  const openFoodEdit = (meal, foodIndex) => {
    const foods = activeFoods(meal);
    setFoodSheet({ mealId: meal.id, foodIndex, item: foods[foodIndex], mealFoods: foods, mealName: meal.name || ix('mealUnnamed') });
    setIsFoodSheetOpen(true);
  };
  const openFoodAdd = (meal) => {
    setFoodSheet({ mealId: meal.id, foodIndex: null, item: null, mealFoods: activeFoods(meal), mealName: meal.name || ix('mealUnnamed') });
    setIsFoodSheetOpen(true);
  };
  const changeFoods = (change) => {
    const { mealId } = foodSheet;
    updateMeal(mealId, (meal) => ({ ...withActiveFoods(meal, change(activeFoods(meal))), confirmed: false }));
    setIsFoodSheetOpen(false);
  };
  const swapFood = (food) => changeFoods((foods) => replaceFoodAt({ foods }, foodSheet.foodIndex, food).foods);
  const removeFood = () => changeFoods((foods) => removeFoodAt({ foods }, foodSheet.foodIndex).foods);
  const addFood = (food) => {
    trackEvent('diet_plan_build_add_food', 'Diet Plan - Build Add Food', 'diet_plan', true);
    changeFoods((foods) => [...foods, food]);
  };

  // ── Saving ──────────────────────────────────────────────────────────

  const dayNames = DAY_KEYS.map((key) => diet(key));

  const save = async () => {
    setDialog(null);
    setIsSaving(true);
    try {
      const payload = planPayload(draft, { origin: isImport ? 'imported' : 'manual', alternate, dayNames });
      await onSave(payload);
      trackEvent(isImport ? 'diet_plan_import_saved' : 'diet_plan_manual_saved', isImport ? 'Diet Plan - Import Saved' : 'Diet Plan - Manual Saved', 'diet_plan', true);
      toast.success(ix('savedToast'), { toastId: 'diet-plan-saved' });
    } catch {
      toast.error(t('notifications.dietPlanSaveError'), { toastId: 'diet-plan-save-error' });
      setIsSaving(false);
    }
  };

  // Free accounts build the plan like anyone; Pro is asked for when it's saved (an import asks at "Ler meu plano")
  const askSave = () => {
    if (!isPro) {
      onUpgrade();
      return;
    }
    const found = draftProblem(draft);
    if (found) {
      if (found.mealId) {
        const at = draft.groups.findIndex((candidate) => candidate.meals.some((meal) => meal.id === found.mealId));
        if (at !== -1) setGroupIndex(at);
        setOpenMealId(found.mealId);
        setTimeout(() => document.getElementById(`mt-di-meal-${found.mealId}`)?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }), 50);
      }
      toast.error(ix(`problem_${found.code}`, { meal: found.name || '' }), { toastId: 'diet-draft-problem' });
      return;
    }
    if (!isImport && !consent) {
      setIsConsentMissing(true);
      toast.error(ix('consentRequiredSave'), { toastId: 'diet-manual-consent' });
      document.getElementById('mt-di-consent')?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      return;
    }
    if (hasPlan) {
      setDialog('replace');
      return;
    }
    save();
  };

  const goBack = () => {
    if (stage === 'reading') cancelReading();
    if (isDirty) {
      setDialog('leave');
      return;
    }
    onBack();
  };

  // ── Pieces ──────────────────────────────────────────────────────────

  const backBar = (
    <div className="mt-di-bar">
      <button type="button" className="mt-di-bar__back" onClick={goBack}>
        <ArrowLeft aria-hidden="true" />
        <span>{t('pages.DietPlan.ui.title')}</span>
      </button>
    </div>
  );

  const consentBox = (text) => (
    <label id="mt-di-consent" className="mt-auth__terms mt-di-consent">
      <input
        type="checkbox"
        checked={consent}
        aria-invalid={isConsentMissing || undefined}
        onChange={(event) => {
          setConsent(event.target.checked);
          setIsConsentMissing(false);
        }}
      />
      <span>
        {text}{' '}
        <a href={t('privacyUrl')} target="_blank" rel="noopener noreferrer">
          {t('standalone.privacyLinkText')}
        </a>
        .
      </span>
    </label>
  );

  const problemText = (found) => {
    if (!found) return null;
    switch (found.code) {
      case 'FILE_TOO_LARGE':
        return found.limitMb ? ix('errorTooLargeLimit', { limit: Math.round(found.limitMb) }) : ix('errorTooLarge');
      case 'TOO_MANY_PAGES':
        return ix('errorPages', { pages: found.pages, limit: found.limit || IMPORT_LIMITS.pdfMaxPages });
      case 'UNSUPPORTED_TYPE':
        return ix('errorType');
      case 'TEXT_TOO_LONG':
        return ix('errorTextLong', { limit: formatNumber(IMPORT_LIMITS.textMaxChars) });
      // The daily AI limits (5 plans read a day, or too many in a minute)
      case 'AI_DAILY_LIMIT':
      case 'AI_RATE_LIMIT':
        return aiLimitText(t, { ...found, isDaily: found.code === 'AI_DAILY_LIMIT' }, { locale: language }).message;
      case 'NETWORK':
        return ix('errorNetwork');
      case 'PRO_REQUIRED':
      case 'CONSENT_REQUIRED':
        return null;
      default:
        return ix('errorRead');
    }
  };

  const renderUpload = () => {
    const isPdf = file?.type === 'application/pdf';
    const found = problemText(problem);
    return (
      <>
        {backBar}
        <div className="mt-di-head">
          <div>
            <h1 className="mt-di-head__title">{ix('importTitle')}</h1>
            <p className="mt-di-head__sub">{ix('importSub')}</p>
          </div>
        </div>
        <div className="mt-di-upload">
          <div className="mt-di-upload__main">
            {isTextMode ? (
              <section className="mt-di-card mt-di-paste">
                <div className="mt-di-paste__head">
                  <h2 className="mt-di-card-title">{ix('pasteTitle')}</h2>
                  <button type="button" className="mt-di-link" onClick={() => setIsTextMode(false)}>
                    <Upload aria-hidden="true" />
                    {ix('pasteSwitchToFile')}
                  </button>
                </div>
                <label className="mt-sr-only" htmlFor="mt-di-text">
                  {ix('pasteTitle')}
                </label>
                <textarea
                  id="mt-di-text"
                  className="mt-di-paste__field"
                  value={text}
                  rows={12}
                  maxLength={IMPORT_LIMITS.textMaxChars}
                  placeholder={ix('pastePlaceholder')}
                  onChange={(event) => setText(event.target.value)}
                />
                <p className="mt-di-paste__count">{ix('pasteCount', { count: formatNumber(text.length), limit: formatNumber(IMPORT_LIMITS.textMaxChars) })}</p>
              </section>
            ) : (
              <section
                className="mt-di-drop"
                onDragOver={(event) => event.preventDefault()}
                onDrop={onDrop}
                aria-labelledby="mt-di-drop-title"
              >
                <PreviewArt kind="sheet" badge="upload" size="lg" />
                <h2 id="mt-di-drop-title" className="mt-di-drop__title">
                  {ix('dropTitle')}
                </h2>
                <p className="mt-di-drop__sub">{ix('dropSub', { limit: Math.round(IMPORT_LIMITS.pdfMaxBytes / 1024 / 1024) })}</p>
                <div className="mt-di-drop__actions">
                  {!isDesktop && (
                    <button type="button" className="mt-btn mt-btn--quiet" onClick={() => cameraInputRef.current?.click()}>
                      <Camera aria-hidden="true" />
                      {ix('takePhoto')}
                    </button>
                  )}
                  <button type="button" className="mt-btn mt-btn--quiet" onClick={() => fileInputRef.current?.click()}>
                    <FileText aria-hidden="true" />
                    {ix('chooseFile')}
                  </button>
                </div>
                <button type="button" className="mt-di-link" onClick={() => setIsTextMode(true)}>
                  <ClipboardPaste aria-hidden="true" />
                  {ix('pasteInstead')}
                </button>
                <input
                  ref={fileInputRef}
                  className="mt-sr-only"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={(event) => {
                    chooseFile(event.target.files?.[0]);
                    event.target.value = '';
                  }}
                />
                <input
                  ref={cameraInputRef}
                  className="mt-sr-only"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={(event) => {
                    chooseFile(event.target.files?.[0]);
                    event.target.value = '';
                  }}
                />
              </section>
            )}

            {file && !isTextMode && (
              <div className={`mt-di-file${problem ? ' is-error' : ''}`}>
                {isPdf ? (
                  <span className="mt-di-file__icon" aria-hidden="true">
                    <FileText />
                  </span>
                ) : (
                  <img className="mt-di-file__thumb" src={fileUrl} alt="" />
                )}
                <span className="mt-di-file__text">
                  <span className="mt-di-file__name">{file.name}</span>
                  <span className="mt-di-file__meta">
                    {isPdf
                      ? [formatSize(file.size, locale), pdfPages ? ix('pdfPages', { count: pdfPages }) : null].filter(Boolean).join(' · ')
                      : file.size < originalSize
                        ? ix('photoReady', { size: formatSize(file.size, locale) })
                        : formatSize(file.size, locale)}
                  </span>
                </span>
                <button type="button" className="mt-icon-btn" onClick={clearFile} aria-label={ix('removeFile')}>
                  <X aria-hidden="true" />
                </button>
              </div>
            )}

            {found && (
              <p className="mt-di-alert" role="alert">
                <CircleAlert aria-hidden="true" />
                <span>{found}</span>
              </p>
            )}

            {consentBox(ix('consentImport'))}

            <div className="mt-di-upload__go">
              <button type="button" className="mt-btn mt-btn--primary" onClick={startReading} disabled={!canRead}>
                <ScanText aria-hidden="true" />
                {ix('readButton')}
                {!isPro && isProKnown && (
                  <span className="mt-btn__pro">
                    <Crown aria-hidden="true" />
                    Pro
                  </span>
                )}
              </button>
            </div>
          </div>

          <aside className="mt-di-card mt-di-tips">
            <h2 className="mt-di-card-title">{ix('tipsTitle')}</h2>
            <ul>
              <li>
                <Camera aria-hidden="true" />
                <span>
                  <b>{ix('tipPhotoStrong')}</b> {ix('tipPhoto')}
                </span>
              </li>
              <li>
                <FileText aria-hidden="true" />
                <span>
                  <b>{ix('tipPdfStrong')}</b> {ix('tipPdf')}
                </span>
              </li>
              <li>
                <ClipboardPaste aria-hidden="true" />
                <span>
                  <b>{ix('tipTextStrong')}</b> {ix('tipText')}
                </span>
              </li>
            </ul>
            <p className="mt-di-tips__foot">{ix('tipsFoot')}</p>
          </aside>
        </div>
      </>
    );
  };

  const renderReading = () => {
    const current = READ_STEP_AT.filter((at) => readSeconds >= at).length - 1;
    return (
      <>
        {backBar}
        <section className="mt-di-reading" role="status" aria-live="polite">
          {fileUrl && file?.type?.startsWith('image/') ? (
            <img className="mt-di-reading__thumb" src={fileUrl} alt="" />
          ) : (
            <PreviewArt kind="sheetReading" size="lg" />
          )}
          <h1 className="mt-di-reading__title">{ix('readingTitle')}</h1>
          <p className="mt-di-reading__sub">{ix('readingSub')}</p>
          <ol className="mt-di-steps">
            {READ_STEPS.map((key, index) => {
              const state = index < current ? 'is-done' : index === current ? 'is-active' : '';
              return (
                <li key={key} className={state}>
                  <span className="mt-di-steps__mark" aria-hidden="true">
                    {index < current ? <Check /> : index === current ? <span className="mt-dp-spinner" /> : null}
                  </span>
                  {ix(key === 'readStepSend' ? (isTextMode ? 'readStepSendText' : file?.type === 'application/pdf' ? 'readStepSendPdf' : key) : key)}
                </li>
              );
            })}
          </ol>
          <button type="button" className="mt-btn mt-btn--quiet" onClick={cancelReading}>
            {ix('cancel')}
          </button>
        </section>
      </>
    );
  };

  const renderUnreadable = () => (
    <>
      {backBar}
      <section className="mt-di-card mt-di-unreadable">
        {source?.kind === 'image' && <img className="mt-di-unreadable__img" src={source.url} alt="" />}
        <div className="mt-di-unreadable__body">
          <h1 className="mt-di-unreadable__title">{ix(source?.kind === 'text' ? 'unreadableTitleText' : 'unreadableTitle')}</h1>
          <p className="mt-di-unreadable__text">
            {unreadReason ? `${unreadReason} ` : ''}
            {ix('unreadableNothingSaved')}
          </p>
          <ul className="mt-di-unreadable__tips">
            <li>
              <Camera aria-hidden="true" />
              <span>
                <b>{ix('unreadableTipPhotoStrong')}</b> {ix('unreadableTipPhoto')}
              </span>
            </li>
            <li>
              <ClipboardPaste aria-hidden="true" />
              <span>
                <b>{ix('unreadableTipTextStrong')}</b> {ix('unreadableTipText')}
              </span>
            </li>
            <li>
              <ListChecks aria-hidden="true" />
              <span>
                <b>{ix('unreadableTipManualStrong')}</b> {ix('unreadableTipManual')}
              </span>
            </li>
          </ul>
          <div className="mt-di-unreadable__actions">
            <button type="button" className="mt-btn mt-btn--primary" onClick={() => tryAgainWith('photo')}>
              <Camera aria-hidden="true" />
              {ix('anotherPhoto')}
            </button>
            <button type="button" className="mt-btn mt-btn--quiet" onClick={() => tryAgainWith('text')}>
              <ClipboardPaste aria-hidden="true" />
              {ix('pasteText')}
            </button>
            <button
              type="button"
              className="mt-btn mt-btn--quiet"
              onClick={() => {
                clearFile();
                setStage('pick');
              }}
            >
              <ListChecks aria-hidden="true" />
              {ix('buildByHand')}
            </button>
          </div>
        </div>
      </section>
    </>
  );

  const renderPick = () => (
    <>
      {backBar}
      <div className="mt-di-head">
        <div>
          <h1 className="mt-di-head__title">{ix('manualTitle')}</h1>
          <p className="mt-di-head__sub">{ix('manualSub')}</p>
        </div>
      </div>
      <section className="mt-di-card mt-di-pick" aria-labelledby="mt-di-pick-title">
        <h2 id="mt-di-pick-title" className="mt-di-card-title">
          {ix('pickTitle')}
        </h2>
        <p className="mt-di-pick__sub">{ix('pickSub')}</p>
        <ul className="mt-di-pick__list">
          {MEAL_PRESETS.map((preset) => (
            <li key={preset.key}>
              <button
                type="button"
                className="mt-di-chip"
                aria-pressed={picked.has(preset.key)}
                onClick={() =>
                  setPicked((current) => {
                    const next = new Set(current);
                    if (next.has(preset.key)) next.delete(preset.key);
                    else next.add(preset.key);
                    return next;
                  })
                }
              >
                {picked.has(preset.key) && <Check aria-hidden="true" />}
                {ix(`meal_${preset.key}`)}
                <small>{preset.time}</small>
              </button>
            </li>
          ))}
        </ul>
        <label className="mt-form-field mt-di-pick__custom">
          <span className="mt-form-field__label">{ix('pickOther')}</span>
          <input value={customMeal} onChange={(event) => setCustomMeal(event.target.value)} placeholder={ix('pickOtherPlaceholder')} maxLength={40} />
        </label>
        <p className="mt-di-pick__same">
          <Repeat aria-hidden="true" />
          {ix('pickSameEveryDay')}
        </p>
        <button type="button" className="mt-btn mt-btn--primary mt-di-pick__go" onClick={startManual}>
          {ix('pickStart')}
        </button>
      </section>
    </>
  );

  const groupLabel = (candidate) => candidate.dayIndexes.map((day) => diet(DAY_SHORT_KEYS[day])).join(', ');

  const optionMeals = meals.filter((meal) => meal.options.length > 1).map((meal) => (language === 'pt' ? meal.name.toLowerCase() : meal.name));
  const listFormat = (items) => new Intl.ListFormat(locale, { type: 'conjunction' }).format(items);

  const saveLabel = allConfirmed || !isImport ? ix('saveButton') : ix('saveAsIs');

  const renderReview = () => {
    const isReview = isImport;
    const highlightMeal = meals.find((meal) => meal.id === (hoveredMealId || openMealId));
    const showOriginal = isReview && source;
    return (
      <>
        {backBar}
        <div className="mt-di-head">
          <div>
            <h1 className="mt-di-head__title">{ix(isReview ? 'reviewTitle' : 'buildTitle')}</h1>
            <p className="mt-di-head__sub">
              {ix(isReview ? 'reviewSub' : 'buildSub', { done: confirmedCount, total: everyMeal.length })}
            </p>
          </div>
          <div className="mt-di-head__actions">
            {isReview && (
              <button type="button" className="mt-btn mt-btn--quiet" onClick={() => setDialog('reread')}>
                <RefreshCw aria-hidden="true" />
                {ix('readAgain')}
              </button>
            )}
            <button type="button" className={`mt-btn ${allConfirmed || !isReview ? 'mt-btn--primary' : 'mt-btn--quiet'}`} onClick={askSave} disabled={isSaving}>
              <Check aria-hidden="true" />
              {saveLabel}
            </button>
          </div>
        </div>

        {showOriginal && (
          <div className="mt-di-switch mt-segment" role="group" aria-label={ix('switchLabel')}>
            <button type="button" aria-pressed={phoneTab === 'read'} onClick={() => setPhoneTab('read')}>
              {ix('switchRead')}
            </button>
            <button type="button" aria-pressed={phoneTab === 'original'} onClick={() => setPhoneTab('original')}>
              {ix('switchOriginal')}
            </button>
          </div>
        )}

        <div className={`mt-di-review${showOriginal ? '' : ' mt-di-review--build'}${phoneTab === 'original' ? ' is-original' : ''}`}>
          {showOriginal ? (
            <div className={`mt-di-review__side${source.kind === 'image' ? ' is-sticky' : ''}`}>
              <OriginalPanel source={source} highlight={source.kind === 'image' ? highlightMeal?.region : null} ix={ix} />
            </div>
          ) : (
            <div className="mt-di-review__side is-sticky mt-di-desk-only">
              <section className="mt-di-card mt-di-day">
                <h2 className="mt-di-card-title">{ix('yourDay')}</h2>
                <ol className="mt-di-day__list">
                  {meals.map((meal, index) => (
                    <li key={meal.id}>
                      <button type="button" className="mt-di-day__row" onClick={() => setOpenMealId(meal.id)} aria-current={openMealId === meal.id || undefined}>
                        <span className={`mt-di-node${meal.confirmed ? ' is-done' : openMealId === meal.id ? ' is-current' : ''}`} aria-hidden="true">
                          {meal.confirmed ? <Check /> : index + 1}
                        </span>
                        <span className="mt-di-day__text">
                          <span className="mt-di-day__name">{meal.name || ix('mealUnnamed')}</span>
                          <span className="mt-di-day__time">{meal.time || ' '}</span>
                        </span>
                        <span className="mt-di-day__kcal">{formatNumber(menuTotals([meal]).calories)} kcal</span>
                      </button>
                    </li>
                  ))}
                </ol>
                <p className="mt-di-day__total">{ix('dayTotal', { kcal: formatNumber(totals.calories) })}</p>
              </section>
            </div>
          )}

          <div className="mt-di-review__main">
            <section className="mt-di-stats" aria-label={ix('statsLabel')}>
              <p>
                <b>{formatNumber(totals.calories)}</b>
                <span>{ix('statKcal')}</span>
              </p>
              <p>
                <b>{Math.round(totals.protein)} g</b>
                <span>{diet('protein').toLowerCase()}</span>
              </p>
              <p>
                <b>{Math.round(totals.carbs)} g</b>
                <span>{diet('carbs').toLowerCase()}</span>
              </p>
              <p>
                <b>{meals.length}</b>
                <span>{ix('statMeals', { count: meals.length })}</span>
              </p>
            </section>

            {isReview && unreadNames.length > 0 && (
              <p className="mt-di-notice">
                <CircleAlert aria-hidden="true" />
                <span>
                  <b>{ix('halfReadTitle', { read: everyMeal.length - unreadNames.length, total: everyMeal.length })}</b>{' '}
                  {ix('halfReadText', { meals: listFormat(unreadNames.map((name) => (language === 'pt' ? name.toLowerCase() : name))) })}
                </span>
              </p>
            )}

            {isReview && flags.length > 0 && (
              <p className="mt-di-notice">
                <CircleAlert aria-hidden="true" />
                <span>
                  <b>{ix('flagsTitle', { count: flags.length })}</b> {[assumedCount && ix('flagsAssumed', { count: assumedCount }), estimateCount && ix('flagsEstimate', { count: estimateCount })].filter(Boolean).join(ix('and'))}
                </span>
              </p>
            )}

            {isReview && (
              <p className="mt-di-info">
                <Repeat aria-hidden="true" />
                <span>
                  {draft.sameEveryDay ? (
                    <>
                      <b>{ix('weekSameTitle')}</b> {ix('weekSameText')}
                    </>
                  ) : (
                    <>
                      <b>{ix('weekDaysTitle', { count: draft.groups.length })}</b> {ix('weekDaysText')}
                      {draft.uncoveredDays.length > 0 && ` ${ix('weekUncovered', { days: listFormat(draft.uncoveredDays.map((day) => diet(DAY_KEYS[day]))) })}`}
                    </>
                  )}
                </span>
              </p>
            )}

            {draft.groups.length > 1 && (
              <div className="mt-di-groups" role="tablist" aria-label={ix('groupsLabel')}>
                {draft.groups.map((candidate, index) => {
                  const done = candidate.meals.length > 0 && candidate.meals.every((meal) => meal.confirmed);
                  return (
                    <button
                      key={groupLabel(candidate)}
                      type="button"
                      role="tab"
                      aria-selected={index === groupIndex}
                      className="mt-di-chip"
                      onClick={() => {
                        setGroupIndex(index);
                        setOpenMealId(candidate.meals.find((meal) => !meal.confirmed)?.id || null);
                      }}
                    >
                      {done && <Check aria-hidden="true" />}
                      {groupLabel(candidate)}
                    </button>
                  );
                })}
              </div>
            )}

            {hasOptions(draft) && (
              <section className="mt-di-card mt-di-ask" aria-labelledby="mt-di-ask-title">
                <h2 id="mt-di-ask-title" className="mt-di-card-title">
                  {ix('askTitle')}
                </h2>
                <p className="mt-di-ask__text">{optionMeals.length ? ix('askText', { meals: listFormat(optionMeals) }) : ix('askTextGeneric')}</p>
                <div className="mt-di-ask__choices" role="radiogroup" aria-labelledby="mt-di-ask-title">
                  <button type="button" role="radio" aria-checked={!alternate} className="mt-di-choice" onClick={() => setAlternate(false)}>
                    <span className="mt-di-choice__title">{ix('askPick')}</span>
                    <span className="mt-di-choice__desc">{ix('askPickDesc')}</span>
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={alternate}
                    className="mt-di-choice"
                    onClick={() => {
                      trackEvent('diet_plan_import_alternate', 'Diet Plan - Alternate Options', 'diet_plan', true);
                      setAlternate(true);
                    }}
                  >
                    <span className="mt-di-choice__title">{ix('askAlternate')}</span>
                    <span className="mt-di-choice__desc">{ix('askAlternateDesc')}</span>
                  </button>
                </div>
              </section>
            )}

            <ol className="mt-di-meals">
              {meals.map((meal, index) => (
                <div key={meal.id} id={`mt-di-meal-${meal.id}`} className="mt-di-meal-anchor">
                  <MealDraftCard
                    meal={meal}
                    number={index + 1}
                    isOpen={openMealId === meal.id}
                    isHighlighted={hoveredMealId === meal.id}
                    isReview={isReview}
                    canShowOriginal={isReview && source?.kind === 'image'}
                    onOpen={() => setOpenMealId(meal.id)}
                    onChange={(changed) => updateMeal(meal.id, () => changed)}
                    onEditFood={(foodIndex) => openFoodEdit(meal, foodIndex)}
                    onAddFood={() => openFoodAdd(meal)}
                    onConfirm={() => confirmMeal(meal.id)}
                    onRemove={() => removeMeal(meal.id)}
                    onShowOriginal={() => {
                      setHoveredMealId(meal.id);
                      setPhoneTab('original');
                      window.scrollTo(0, 0);
                    }}
                    onHover={isReview && source?.kind === 'image' && isDesktop ? setHoveredMealId : null}
                  />
                </div>
              ))}
            </ol>

            <section className="mt-di-card mt-di-add" aria-labelledby="mt-di-add-title">
              <h2 id="mt-di-add-title" className="mt-di-card-title">
                {ix('addMealTitle')}
              </h2>
              <ul className="mt-di-pick__list">
                {MEAL_PRESETS.filter((preset) => !meals.some((meal) => meal.name.trim().toLowerCase() === ix(`meal_${preset.key}`).toLowerCase())).map((preset) => (
                  <li key={preset.key}>
                    <button type="button" className="mt-di-chip" onClick={() => addMeal(ix(`meal_${preset.key}`), preset.time)}>
                      <Plus aria-hidden="true" />
                      {ix(`meal_${preset.key}`)}
                    </button>
                  </li>
                ))}
                <li>
                  <button type="button" className="mt-di-chip" onClick={() => addMeal('', null)}>
                    <Plus aria-hidden="true" />
                    {ix('addMealOther')}
                  </button>
                </li>
              </ul>
            </section>

            {draft.notes?.length > 0 && (
              <section className="mt-di-card mt-di-notes" aria-labelledby="mt-di-notes-title">
                <h2 id="mt-di-notes-title" className="mt-di-card-title">
                  {ix('notesTitle')}
                </h2>
                <ul>
                  {draft.notes.map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </section>
            )}

            {!isImport && consentBox(ix('consentManual'))}

            <div className="mt-di-review__end">
              <button type="button" className={`mt-btn ${allConfirmed || !isReview ? 'mt-btn--primary' : 'mt-btn--quiet'}`} onClick={askSave} disabled={isSaving}>
                <Check aria-hidden="true" />
                {saveLabel}
              </button>
            </div>
          </div>
        </div>

        {/* Phones: the open meal's two actions under the thumb; once every meal is confirmed, saving */}
        <div className="mt-di-savebar">
          {openMealId && meals.some((meal) => meal.id === openMealId) ? (
            <>
              <button type="button" className="mt-btn mt-btn--quiet" onClick={() => openFoodAdd(meals.find((meal) => meal.id === openMealId))}>
                <Plus aria-hidden="true" />
                {ix('addFoodShort')}
              </button>
              <button
                type="button"
                className="mt-btn mt-btn--primary"
                onClick={() => confirmMeal(openMealId)}
                disabled={meals.find((meal) => meal.id === openMealId)?.options.every((option) => option.foods.length === 0)}
              >
                <Check aria-hidden="true" />
                {ix(isReview ? 'confirmNext' : 'mealDone')}
              </button>
            </>
          ) : (
            <button type="button" className="mt-btn mt-btn--primary" onClick={askSave} disabled={isSaving}>
              <Check aria-hidden="true" />
              {saveLabel}
            </button>
          )}
        </div>
      </>
    );
  };

  let body;
  if (stage === 'upload') body = renderUpload();
  else if (stage === 'reading') body = renderReading();
  else if (stage === 'unreadable') body = renderUnreadable();
  else if (stage === 'pick') body = renderPick();
  else body = renderReview();


  return (
    <div className="mt-di">
      {body}

      <FoodEditSheet
        isOpen={isFoodSheetOpen}
        onOpenChange={setIsFoodSheetOpen}
        food={foodSheet?.item}
        mealName={foodSheet?.mealName}
        mealFoods={foodSheet?.mealFoods}
        canRemove
        onSwap={swapFood}
        onAmount={swapFood}
        onRemove={removeFood}
        onAdd={foodSheet && !foodSheet.item ? addFood : undefined}
        substitutes={foodSheet?.item ? substitutesFor(draft?.substitutions, foodSheet.item) : undefined}
        keepSource
      />

      <Modal
        isOpen={!!dialog}
        onOpenChange={(open) => !open && setDialog(null)}
        placement="center"
        hideCloseButton
        aria-label={dialog ? ix(`dialog_${dialog}_title`) : undefined}
        classNames={{ base: 'mt-root mt-dialog', backdrop: 'mt-dialog__backdrop' }}
      >
        <ModalContent>
          {() =>
            dialog && (
              <div className="mt-dialog__body">
                <h2 className="mt-dialog__title">{ix(`dialog_${dialog}_title`)}</h2>
                <p className="mt-dialog__text">{ix(`dialog_${dialog}_text`)}</p>
                <div className="mt-dialog__actions">
                  <button type="button" className="mt-btn mt-btn--quiet" onClick={() => setDialog(null)}>
                    {ix('dialogCancel')}
                  </button>
                  <button
                    type="button"
                    className="mt-btn mt-btn--primary"
                    onClick={() => {
                      if (dialog === 'replace') save();
                      else if (dialog === 'leave') {
                        setDialog(null);
                        onDirtyChange?.(false);
                        onBack();
                      } else if (dialog === 'reread') {
                        setDialog(null);
                        setDraft(null);
                        clearFile();
                        setStage('upload');
                      }
                    }}
                  >
                    {ix(`dialog_${dialog}_confirm`)}
                  </button>
                </div>
              </div>
            )
          }
        </ModalContent>
      </Modal>
    </div>
  );
};

